// Lista da guia Diversos (laudos avulsos). Mesmo formato das outras guias:
// busca os proprios dados e cuida dos botoes de PDF e assinatura.
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Card, Tag, message, Popconfirm, Input } from 'antd';
import { EditOutlined, FilePdfOutlined, EyeOutlined, SafetyCertificateOutlined,
  DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';

export default function ListaDiversos() {
  const navigate = useNavigate();
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get('/laudos-avulsos');
      setLista(r.data || []);
    } catch (e) {
      message.error('Erro ao carregar: ' + (e.response?.data?.erro || e.message));
    } finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const gerarPDF = async (l) => {
    try {
      message.loading({ content: 'Gerando PDF...', key: 'pdfdv', duration: 0 });
      try {
        await api.get('/laudos-avulsos/' + l.id + '/pdf', { responseType: 'blob', timeout: 180000 });
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        message.destroy('pdfdv');
        if (!window.confirm('O laudo ' + (l.codigo || '') + ' já está assinado digitalmente. Gerar um PDF novo APAGA a assinatura. Continuar?')) return;
        message.loading({ content: 'Gerando PDF...', key: 'pdfdv', duration: 0 });
        await api.get('/laudos-avulsos/' + l.id + '/pdf?forcar=1', { responseType: 'blob', timeout: 180000 });
      }
      message.success({ content: 'PDF gerado e salvo!', key: 'pdfdv' });
      carregar();
    } catch (e) {
      message.error({ content: 'Erro ao gerar PDF: ' + (e.response?.data?.erro || e.message), key: 'pdfdv' });
    }
  };

  const verPDF = async (l) => {
    try {
      message.loading({ content: 'Carregando PDF...', key: 'vizdv', duration: 0 });
      const r = await api.get('/laudos-avulsos/' + l.id + '/pdf/visualizar', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      message.success({ content: 'PDF aberto!', key: 'vizdv' });
    } catch (e) {
      message.destroy('vizdv');
      message.warning('PDF não gerado ainda. Use o botão PDF para gerar.');
    }
  };

  const assinar = async (l) => {
    if (!window.confirm('Assinar digitalmente o laudo ' + (l.codigo || '') + '? Pode levar até 1 minuto.')) return;
    try {
      message.loading({ content: 'Assinando PDF com certificado A1...', key: 'signdv', duration: 0 });
      await api.post('/laudos-avulsos/' + l.id + '/pdf/assinar', {}, { timeout: 120000 });
      message.success({ content: 'PDF assinado com sucesso!', key: 'signdv' });
      carregar();
    } catch (e) {
      message.error({ content: 'Erro ao assinar: ' + (e.response?.data?.erro || e.message), key: 'signdv' });
    }
  };

  const excluir = async (l) => {
    try {
      await api.delete('/laudos-avulsos/' + l.id);
      message.success('Laudo excluído');
      carregar();
    } catch (e) {
      message.error('Erro ao excluir: ' + (e.response?.data?.erro || e.message));
    }
  };

  const t = busca.toLowerCase();
  const filtrada = !t ? lista : lista.filter((l) =>
    [l.codigo, l.titulo, l.tag, l.numero_os, l.razao_faturamento, l.razao_social, l.nome_cliente]
      .some((c) => String(c || '').toLowerCase().includes(t)));

  const data = (v) => (v ? dayjs(String(v).substring(0, 10)).format('DD/MM/YYYY') : '-');

  const colunas = [
    { title: 'Código', dataIndex: 'codigo', width: 120, render: (v) => v || <Tag>sem código</Tag> },
    { title: 'Título', dataIndex: 'titulo', ellipsis: true, render: (v) => v || '-' },
    { title: 'Cliente', width: 220, ellipsis: true,
      render: (_, l) => l.razao_faturamento || l.razao_social || l.nome_cliente || '-' },
    { title: 'TAG', dataIndex: 'tag', width: 110, render: (v) => v || '-' },
    { title: 'OS', dataIndex: 'numero_os', width: 110, render: (v) => v || '-' },
    { title: 'Data', dataIndex: 'data_inspecao', width: 120, render: data,
      sorter: (a, b) => String(a.data_inspecao || '').localeCompare(String(b.data_inspecao || '')) },
    { title: 'Modificado', dataIndex: 'atualizado_em', width: 130,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-'),
      sorter: (a, b) => String(a.atualizado_em || '').localeCompare(String(b.atualizado_em || '')),
      defaultSortOrder: 'descend' },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v, l) => (
      <Space direction="vertical" size={0}>
        <Tag color={v === 'concluido' ? 'green' : 'default'}>{v === 'concluido' ? 'Concluído' : 'Rascunho'}</Tag>
        {l.pdf_assinado_em && <Tag color="blue">assinado</Tag>}
      </Space>
    ) },
    { title: 'Ações', width: 210, render: (_, l) => (
      <Space size={4}>
        <Button size="small" icon={<EditOutlined />} onClick={() => navigate('/laudos-avulsos/' + l.id)} />
        <Button size="small" icon={<FilePdfOutlined />} onClick={() => gerarPDF(l)} />
        <Button size="small" icon={<EyeOutlined />} onClick={() => verPDF(l)} />
        <Button size="small" icon={<SafetyCertificateOutlined />} onClick={() => assinar(l)} />
        <Popconfirm title="Excluir este laudo?" onConfirm={() => excluir(l)} okText="Excluir" cancelText="Não">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ) }
  ];

  return (
    <Card>
      <Space style={{ marginBottom: 12 }}>
        <Input prefix={<SearchOutlined />} placeholder="Buscar por código, título, TAG, cliente ou OS"
          value={busca} onChange={(e) => setBusca(e.target.value)} style={{ width: 380 }} allowClear />
        <Button onClick={carregar}>Atualizar</Button>
      </Space>
      <Table rowKey="id" size="small" loading={carregando} columns={colunas}
        dataSource={filtrada} pagination={{ pageSize: 20, showSizeChanger: true }} />
    </Card>
  );
}
